import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { api, formatApiError } from "../lib/api";
import { Flag } from "lucide-react";
import { toast } from "sonner";

const REASONS = [
  { value: "doxxing", label: "Doxxing / personal info", urgent: true },
  { value: "illegal", label: "Illegal content or activity", urgent: true },
  { value: "harassment", label: "Harassment or hateful conduct", urgent: true },
  { value: "spam", label: "Spam or advertising" },
  { value: "misinformation", label: "Misinformation / unsafe advice" },
  { value: "inappropriate", label: "Inappropriate / NSFW" },
  { value: "impersonation", label: "Impersonation" },
  { value: "other", label: "Something else" },
];

/**
 * Small flag button that opens a dialog for reporting a user, topic, or reply.
 * Renders inline; wrap it in your own container. Handles submission + toasts.
 */
export default function ReportButton({ targetType, targetId, label = "Report", compact = false, testid }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("spam");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/reports", {
        target_type: targetType,
        target_id: targetId,
        reason,
        notes: (notes || "").trim() || null,
        context_url: typeof window !== "undefined" ? window.location.pathname + window.location.search : null,
      });
      toast.success("Report sent to admins — thank you");
      setOpen(false);
      setNotes("");
      setReason("spam");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}
        data-testid={testid || `report-btn-${targetType}-${targetId}`}
        title={`Report this ${targetType}`}
        className={compact
          ? "inline-flex items-center justify-center w-6 h-6 text-slate-400 hover:text-red-400 transition-colors"
          : "inline-flex items-center gap-1 border border-slate-200 text-slate-600 hover:border-red-500/60 hover:text-red-400 px-2 h-7 text-[10px] uppercase tracking-widest font-mono-num transition-colors"}
      >
        <Flag className={compact ? "w-3 h-3" : "w-3 h-3"} />
        {!compact && label}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white border border-slate-200 rounded-none text-slate-900 max-w-md" data-testid="report-dialog">
          <DialogHeader>
            <DialogTitle className="font-display font-black uppercase tracking-tight flex items-center gap-2">
              <Flag className="w-4 h-4 text-red-400" /> Report this {targetType}
            </DialogTitle>
            <DialogDescription className="text-slate-600 text-sm">
              Send a note to the site admins. Only they'll see it.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <div className="font-mono-num text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-2">// Reason</div>
              <div className="grid gap-1.5">
                {REASONS.map((r) => (
                  <label key={r.value} className={`flex items-center gap-2 border px-3 py-2 cursor-pointer transition-colors ${
                    reason === r.value
                      ? "border-[#0D9488] bg-[#0D9488]/[0.05]"
                      : r.urgent
                        ? "border-red-200 hover:border-red-400 bg-red-50/40"
                        : "border-slate-200 hover:border-slate-300"
                  }`}>
                    <input
                      type="radio"
                      name="reason"
                      value={r.value}
                      checked={reason === r.value}
                      onChange={() => setReason(r.value)}
                      className="accent-[#0D9488]"
                      data-testid={`report-reason-${r.value}`}
                    />
                    <span className="text-sm text-slate-900 flex-1">{r.label}</span>
                    {r.urgent && (
                      <span className="text-[9px] font-mono-num uppercase tracking-widest font-black text-red-600 bg-red-100 border border-red-300 px-1.5 py-0.5">Urgent</span>
                    )}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <div className="font-mono-num text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-2">// Notes (optional)</div>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                maxLength={1000}
                placeholder="Anything the admin should know…"
                className="w-full bg-slate-50 border border-slate-200 rounded-none px-3 py-2 text-sm text-slate-900 placeholder-zinc-600 outline-none focus:border-[#0D9488] resize-none"
                data-testid="report-notes"
              />
              <div className="font-mono-num text-[10px] text-slate-400 uppercase tracking-widest mt-1 text-right">{notes.length}/1000</div>
            </div>
          </form>
          <DialogFooter>
            <button type="button" onClick={() => setOpen(false)} className="border border-slate-200 hover:border-zinc-600 text-slate-800 px-3 h-9 text-xs uppercase tracking-widest font-semibold" data-testid="report-cancel">Cancel</button>
            <button type="button" onClick={submit} disabled={busy} className="bg-red-500 hover:bg-red-600 text-white px-4 h-9 text-xs uppercase tracking-widest font-semibold disabled:opacity-50" data-testid="report-submit">
              <Flag className="w-3 h-3 inline mr-1.5" /> {busy ? "Sending…" : "Send report"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
